import {
  type HfsAction,
  type HfsOperationStatus,
  HfsMissionSettingsActionEnum,
  HfsSettingsActionEnum,
} from 'fleet-data-model';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AssetOperationExecuteResponse } from '@fleet-bffs/api-contract';
import {
  checkHfsActionMatch,
  checkIfOperationIsActive,
  checkIfOperationIsFailed,
  importHfsOperationStatus,
  reflectOperationCompletion,
  reflectOperationError,
} from '~/domain/assets/utils/hfs-operations-utils';
import { getHfsOperationPayloadMemoTimeout } from '~/domain/assets/utils/hfs-operation-timeouts';
import { filterRelevantPayloadItems } from '~/domain/roboticmower-details/utils';
import type { MissionUpdateParams } from '~/api/pattern-selector-api';
import { UpdateMissionTypeEnum } from '~/domain/roboticmower-details/models/UpdateMissionTypeEnum';
import { type Operation, OperationStatus } from '../../models';

export interface MowerSettingsOperationPayload {
  operationId: string;
  iprId: string;
  completionDate?: Date;
  expiryTimestamp?: number;
}
export interface MissionSettingsOperationPayload extends MowerSettingsOperationPayload {
  missionId: number;
}

export interface MowingBehaviourSettingsState
  extends Omit<MissionUpdateParams, 'missionId'>,
    MissionSettingsOperationPayload {}

export interface MissionEnabledState extends MissionSettingsOperationPayload {
  enabled: boolean;
}

export interface MissionAugmentByVisionState extends MissionSettingsOperationPayload {
  augmentByVisionEnabled: boolean;
}

export interface ScheduleTask {
  taskId: number;
  start: number;
  duration: number;
  useOnDays: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
}

export interface MissionScheduleState extends MissionSettingsOperationPayload {
  tasksList: ScheduleTask[];
}

export interface MissionCuttingHeightState extends MissionSettingsOperationPayload {
  cuttingHeight: number;
}

export interface MowerCuttingHeightState extends MowerSettingsOperationPayload {
  cuttingHeight: number;
}

export interface MowerScheduleState extends MowerSettingsOperationPayload {
  tasksList: ScheduleTask[];
}

export const initialState: Fields = {
  operations: [],
  mowingBehaviourSettings: [],
  missionEnabled: [],
  missionAugmentByVision: [],
  missionCuttingHeight: [],
  mowerCuttingHeight: [],
  missionSchedule: [],
  mowerSchedule: [],
};

export interface Fields {
  operations: Operation[];
  mowingBehaviourSettings: MowingBehaviourSettingsState[];
  missionEnabled: MissionEnabledState[];
  missionAugmentByVision: MissionAugmentByVisionState[];
  missionCuttingHeight: MissionCuttingHeightState[];
  missionSchedule: MissionScheduleState[];
  mowerCuttingHeight: MowerCuttingHeightState[];
  mowerSchedule: MowerScheduleState[];
}

const toOperationStatus = (status: HfsOperationStatus): OperationStatus => OperationStatus[status];

const registerSettingsState = <T extends MowerSettingsOperationPayload>(
  state: Fields,
  targetArray: keyof Fields,
  payload: T,
  hfsActionId: string | undefined
): void => {
  const array = state[targetArray] as unknown as T[];
  if (hfsActionId === undefined) {
    array.push(payload);
  } else {
    const timeout = getHfsOperationPayloadMemoTimeout(hfsActionId);
    array.push({
      ...payload,
      expiryTimestamp: Date.now() + timeout,
    } as T);
    const matchingOperation = state.operations.find(
      (item: Operation) => item.operationId === payload.operationId
    );
    if (matchingOperation) {
      matchingOperation.failureTimeout = timeout;
    }
  }
};

const filterExpiredPayloads = <T extends MowerSettingsOperationPayload>(items: T[]): T[] =>
  items.filter((item) => item.expiryTimestamp === undefined || item.expiryTimestamp > Date.now());

export const getEarliestExpiryTimestamp = (state: Fields) => {
  const collectExpiryTimestamps = (entries: MowerSettingsOperationPayload[]) =>
    entries
      .map((item) => item.expiryTimestamp)
      .filter((expiryTimestamp): expiryTimestamp is number => expiryTimestamp !== undefined);
  const items = [
    ...collectExpiryTimestamps(state.missionCuttingHeight),
    ...collectExpiryTimestamps(state.missionSchedule),
    ...collectExpiryTimestamps(state.mowerCuttingHeight),
    ...collectExpiryTimestamps(state.mowerSchedule),
    ...collectExpiryTimestamps(state.mowingBehaviourSettings),
    ...collectExpiryTimestamps(state.missionEnabled),
    ...collectExpiryTimestamps(state.missionAugmentByVision),
  ];
  return items.length === 0 ? 0 : Math.min(...items);
};

const reducer = createSlice({
  name: 'assets-hfs-operations',
  initialState,
  reducers: {
    registerOperation: (
      state,
      action: PayloadAction<
        Omit<
          Operation,
          | 'startTimestamp'
          | 'status'
          | 'failureTimeout'
          | 'needsAdditionalConfirmation'
          | 'executionType'
        > & {
          failureTimeout?: number;
          needsAdditionalConfirmation?: boolean;
          executionType?: 'INDIVIDUAL' | 'BATCH';
        }
      >
    ) => {
      state.operations.push({
        startTimestamp: Date.now(),
        executionType: 'INDIVIDUAL',
        status: OperationStatus.Posting,
        needsAdditionalConfirmation: false,
        failureTimeout: 300_000,
        ...action.payload,
      });
    },
    clearOperation: (state, action: PayloadAction<{ iprId: string; hfsActionId: string }>) => {
      const { hfsActionId, iprId } = action.payload;
      state.operations = state.operations.filter(
        (item: Operation) => !(item.iprId === iprId && item.hfsActionId === hfsActionId)
      );
    },
    clearOperationById: (state, action: PayloadAction<string>) => {
      state.operations = state.operations.filter(
        (item: Operation) => item.operationId !== action.payload
      );
    },
    clearMissionOperation: (
      state,
      action: PayloadAction<{ iprId: string; hfsActionId: string; missionId: number }>
    ) => {
      const { hfsActionId, iprId, missionId } = action.payload;
      state.operations = state.operations.filter(
        (item: Operation) =>
          !(
            item.missionId === missionId &&
            item.iprId === iprId &&
            item.hfsActionId === hfsActionId
          )
      );
    },
    clearOperations: (state, action: PayloadAction<{ iprId: string; hfsActionsIds: string[] }>) => {
      const { hfsActionsIds, iprId } = action.payload;
      state.operations = state.operations.filter(
        (item: Operation) => !(item.iprId === iprId && hfsActionsIds.includes(item.hfsActionId))
      );
    },
    clearOperationsByStatuses: (
      state,
      action: PayloadAction<{ iprId: string; hfsActionsIds: string[]; statuses: OperationStatus[] }>
    ) => {
      const { hfsActionsIds, iprId, statuses } = action.payload;
      state.operations = state.operations.filter(
        (item: Operation) =>
          !(
            item.iprId === iprId &&
            hfsActionsIds.includes(item.hfsActionId) &&
            statuses.includes(item.status)
          )
      );
    },
    updateOperation: (
      state,
      action: PayloadAction<
        Omit<
          Operation,
          'startTimestamp' | 'failureTimeout' | 'needsAdditionalConfirmation' | 'executionType'
        >
      >
    ) => {
      const { iprId, hfsActionId, status } = action.payload;
      state.operations = state.operations.map((item: Operation) =>
        item.iprId === iprId && item.hfsActionId === hfsActionId ? { ...item, status } : item
      );
    },
    setResultNotificationSent: (state, action: PayloadAction<string>) => {
      state.operations = state.operations.map((item: Operation) =>
        item.operationId === action.payload ? { ...item, resultNotificationSent: true } : item
      );
    },
    updateMissionOperation: (
      state,
      action: PayloadAction<
        Omit<
          Operation,
          'startTimestamp' | 'failureTimeout' | 'needsAdditionalConfirmation' | 'executionType'
        >
      >
    ) => {
      const { iprId, hfsActionId, status, missionId } = action.payload;
      state.operations = state.operations.map((item: Operation) =>
        item.missionId === missionId && item.iprId === iprId && item.hfsActionId === hfsActionId
          ? { ...item, status }
          : item
      );
    },
    updateOperationStatusById: (
      state,
      action: PayloadAction<{
        operationId: string;
        status: OperationStatus;
      }>
    ) => {
      const { status, operationId } = action.payload;
      state.operations = state.operations.map((item: Operation) => {
        if (item.operationId !== operationId) {
          return item;
        }
        return { ...item, status: importHfsOperationStatus(status, item) };
      });
    },
    handleOperationExecuteResponse: (
      state,
      action: PayloadAction<{
        iprId: string;
        missionId?: number;
        result: AssetOperationExecuteResponse;
      }>
    ) => {
      const {
        iprId,
        missionId,
        result: { hfsActionId, status, supportedStates, operationId, timeout },
      } = action.payload;
      const applyExecuteResponse = (item: Operation): Operation => {
        const applied = {
          ...item,
          status: status === undefined ? undefined : toOperationStatus(status),
          supportedStates: supportedStates?.map(toOperationStatus),
          operationId,
          failureTimeout: timeout === undefined ? item.failureTimeout : timeout * 1000,
        };
        // @ts-expect-error omitted status wipes Operation.status
        return applied;
      };
      state.operations = state.operations.map((item: Operation) => {
        if (
          item.iprId === iprId &&
          item.status === OperationStatus.Posting &&
          checkHfsActionMatch(item, hfsActionId) &&
          item.operationId === undefined &&
          item.missionId === missionId
        ) {
          return applyExecuteResponse(item);
        }
        return item;
      });
    },
    assignOperationId: (
      state,
      action: PayloadAction<{
        iprId: string;
        hfsActionId: string;
        operationId: string;
        status: OperationStatus;
      }>
    ) => {
      const { iprId, hfsActionId, status, operationId } = action.payload;
      state.operations = state.operations.map((item: Operation) =>
        item.iprId === iprId && item.hfsActionId === hfsActionId && item.operationId === undefined
          ? { ...item, status, operationId }
          : item
      );
    },
    assignMissionOperationId: (
      state,
      action: PayloadAction<{
        iprId: string;
        hfsActionId: string;
        operationId: string;
        missionId: number;
        status: OperationStatus;
      }>
    ) => {
      const { iprId, hfsActionId, status, missionId, operationId } = action.payload;
      state.operations = state.operations.map((item: Operation) =>
        item.missionId === missionId &&
        item.iprId === iprId &&
        item.hfsActionId === hfsActionId &&
        item.operationId === undefined
          ? { ...item, status, operationId }
          : item
      );
    },
    outdateOperations: (state) => {
      const currentTimestamp = Date.now();
      const checkEligibility = (operation) =>
        (checkIfOperationIsActive(operation) || operation.needsAdditionalConfirmation) &&
        operation.startTimestamp < currentTimestamp - operation.failureTimeout;
      const eligible = state.operations.filter(checkEligibility);
      if (eligible.length > 0) {
        state.operations = state.operations.map((operation) =>
          checkEligibility(operation)
            ? { ...operation, status: OperationStatus.Timeout }
            : operation
        );
      }
    },
    registerMowingBehaviourSettings: (
      state,
      action: PayloadAction<MowingBehaviourSettingsState>
    ) => {
      registerSettingsState(
        state,
        'mowingBehaviourSettings',
        action.payload,
        UpdateMissionTypeEnum.UpdateMissionBehaviour
      );
    },
    registerMissionSchedule: (state, action: PayloadAction<MissionScheduleState>) => {
      registerSettingsState(
        state,
        'missionSchedule',
        action.payload,
        UpdateMissionTypeEnum.UpdateMissionSchedule
      );
    },
    registerMissionCuttingHeight: (state, action: PayloadAction<MissionCuttingHeightState>) => {
      registerSettingsState(
        state,
        'missionCuttingHeight',
        action.payload,
        UpdateMissionTypeEnum.UpdateMissionCuttingHeight
      );
    },
    registerMowerCuttingHeight: (state, action: PayloadAction<MowerCuttingHeightState>) => {
      registerSettingsState(
        state,
        'mowerCuttingHeight',
        action.payload,
        HfsSettingsActionEnum.MOWER_CUTTING_HEIGHT
      );
    },
    registerMowerSchedule: (state, action: PayloadAction<MowerScheduleState>) => {
      registerSettingsState(
        state,
        'mowerSchedule',
        action.payload,
        HfsSettingsActionEnum.MOWER_SCHEDULE
      );
    },
    registerMissionEnabled: (state, action: PayloadAction<MissionEnabledState>) => {
      registerSettingsState(
        state,
        'missionEnabled',
        action.payload,
        UpdateMissionTypeEnum.UpdateMissionEnabled
      );
    },
    registerMissionAugmentByVision: (state, action: PayloadAction<MissionAugmentByVisionState>) => {
      registerSettingsState(
        state,
        'missionAugmentByVision',
        action.payload,
        UpdateMissionTypeEnum.UpdateMissionAugmentByVision
      );
    },
    cleanupForMissionAugmentByVision: (
      state,
      action: PayloadAction<{ iprId: string; missionId: number }>
    ) => {
      state.missionAugmentByVision = state.missionAugmentByVision.filter(
        (item) =>
          !(item.iprId === action.payload.iprId && item.missionId === action.payload.missionId)
      );
    },
    cleanupForMissionCuttingHeight: (
      state,
      action: PayloadAction<{ iprId: string; missionId: number }>
    ) => {
      state.missionCuttingHeight = state.missionCuttingHeight.filter(
        (item) =>
          !(item.iprId === action.payload.iprId && item.missionId === action.payload.missionId)
      );
    },
    cleanupForMowerCuttingHeight: (state, action: PayloadAction<{ iprId: string }>) => {
      state.mowerCuttingHeight = state.mowerCuttingHeight.filter(
        (item) => item.iprId !== action.payload.iprId
      );
    },
    cleanupForMowerSchedule: (state, action: PayloadAction<{ iprId: string }>) => {
      state.mowerSchedule = state.mowerSchedule.filter(
        (item) => item.iprId !== action.payload.iprId
      );
    },
    cleanupForMissionSchedule: (
      state,
      action: PayloadAction<{ iprId: string; missionId: number }>
    ) => {
      state.missionSchedule = state.missionSchedule.filter(
        (item) =>
          !(item.iprId === action.payload.iprId && item.missionId === action.payload.missionId)
      );
    },
    cleanupForMowingBehaviourSettingsMission: (
      state,
      action: PayloadAction<{ iprId: string; missionId: number }>
    ) => {
      state.mowingBehaviourSettings = state.mowingBehaviourSettings.filter(
        (item) =>
          !(item.iprId === action.payload.iprId && item.missionId === action.payload.missionId)
      );
    },
    cleanupForMissionEnabled: (
      state,
      action: PayloadAction<{ iprId: string; missionId: number }>
    ) => {
      state.missionEnabled = state.missionEnabled.filter(
        (item) =>
          !(item.iprId === action.payload.iprId && item.missionId === action.payload.missionId)
      );
    },
    reflectOperationStatusChange: (
      state,
      action: PayloadAction<{ operationId: string; status: OperationStatus | HfsOperationStatus }>
    ) => {
      const { status, operationId } = action.payload;
      const operation = state.operations.find((item) => item.operationId === operationId);
      if (operation?.operationId !== undefined) {
        const updatedOperation = {
          ...operation,
          status: importHfsOperationStatus(status, operation),
        };
        state.operations = state.operations.map((item: Operation) =>
          item.operationId === operationId ? updatedOperation : item
        );

        if (checkIfOperationIsFailed(updatedOperation)) {
          reflectOperationError(state, operationId);
        } else if (!checkIfOperationIsActive(updatedOperation)) {
          reflectOperationCompletion(state, operationId);
        }
      }
    },
    /*
     * Performs analysis of hfs actions change to detect changes across MOWER_AREA_SCHEDULE / MOWER_AREA_CUTTING_HEIGHT and preforms cleanup of missionSchedule & missionCuttingHeight states once change propagation has been detected.
     * Applicable only for those MOWER_AREA_* actions which are delivered via AssetHfsActionsUpdated
     * */
    reflectAssetHfsActionsUpdate: (
      state,
      action: PayloadAction<{
        assetId: AssetId;
        previous: HfsAction[];
        updated: HfsAction[];
      }>
    ) => {
      const { assetId, previous, updated } = action.payload;

      // Detect presence of schedule actions among updated
      const latestScheduleActions = updated.filter(
        (hfsAction) => hfsAction.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_SCHEDULE
      );
      const scheduleActionsChanged = latestScheduleActions.filter(
        (hfsAction) =>
          JSON.stringify(
            previous.find(
              (prevAction) =>
                prevAction.hfsAction === hfsAction.hfsAction &&
                hfsAction.metadata?.missionId === prevAction.metadata?.missionId
            )?.settingData
          ) !== JSON.stringify(hfsAction.settingData)
      );
      if (scheduleActionsChanged.length > 0) {
        // Clean local states for schedule
        const filtered = state.missionSchedule.filter(
          (item) =>
            !(
              item.iprId === assetId &&
              scheduleActionsChanged.some(
                (changedAction) => changedAction.metadata?.missionId === item.missionId
              )
            )
        );
        if (filtered.length < state.missionSchedule.length) {
          state.missionSchedule = filtered;
        }
      }

      // Detect presence of cutting height actions among updated
      const latestCuttingHeightActions = updated.filter(
        (hfsAction) =>
          hfsAction.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_CUTTING_HEIGHT
      );
      const cuttingHeightActionsChanged = latestCuttingHeightActions.filter(
        (hfsAction) =>
          JSON.stringify(
            previous.find(
              (prevAction) =>
                prevAction.hfsAction === hfsAction.hfsAction &&
                hfsAction.metadata?.missionId === prevAction.metadata?.missionId
            )?.settingData
          ) !== JSON.stringify(hfsAction.settingData)
      );
      if (cuttingHeightActionsChanged.length > 0) {
        const filtered = state.missionCuttingHeight.filter(
          (item) =>
            !(
              item.iprId === assetId &&
              cuttingHeightActionsChanged.some(
                (changedAction) => changedAction.metadata?.missionId === item.missionId
              )
            )
        );
        if (filtered.length < state.missionCuttingHeight.length) {
          state.missionCuttingHeight = filtered;
        }
      }

      // Detect presence of cutting height actions among updated
      const latestMowerCuttingHeightActions = updated.filter(
        (hfsAction) => hfsAction.hfsAction === HfsSettingsActionEnum.MOWER_CUTTING_HEIGHT
      );
      const mowerCuttingHeightActionsChanged = latestMowerCuttingHeightActions.filter(
        (hfsAction) =>
          JSON.stringify(
            previous.find((prevAction) => prevAction.hfsAction === hfsAction.hfsAction)?.settingData
          ) !== JSON.stringify(hfsAction.settingData)
      );
      if (mowerCuttingHeightActionsChanged.length > 0) {
        const filtered = state.mowerCuttingHeight.filter((item) => item.iprId !== assetId);
        if (filtered.length < state.mowerCuttingHeight.length) {
          state.mowerCuttingHeight = filtered;
        }
      }

      // Detect presence of schedule actions among updated
      const latestMowerScheduleActions = updated.filter(
        (hfsAction) => hfsAction.hfsAction === HfsSettingsActionEnum.MOWER_SCHEDULE
      );
      const mowerScheduleActionsChanged = latestMowerScheduleActions.filter(
        (hfsAction) =>
          JSON.stringify(
            previous.find((prevAction) => prevAction.hfsAction === hfsAction.hfsAction)?.settingData
          ) !== JSON.stringify(hfsAction.settingData)
      );
      if (mowerScheduleActionsChanged.length > 0) {
        const filtered = state.mowerSchedule.filter((item) => item.iprId !== assetId);
        if (filtered.length < state.mowerSchedule.length) {
          state.mowerSchedule = filtered;
        }
      }
    },
    cleanupEffectiveOperations: (state) => {
      state.mowingBehaviourSettings = filterRelevantPayloadItems(
        state.mowingBehaviourSettings,
        UpdateMissionTypeEnum.UpdateMissionBehaviour
      ) as MowingBehaviourSettingsState[];
      state.missionEnabled = filterRelevantPayloadItems(
        state.missionEnabled,
        UpdateMissionTypeEnum.UpdateMissionEnabled
      ) as MissionEnabledState[];
      state.missionAugmentByVision = filterRelevantPayloadItems(
        state.missionAugmentByVision,
        UpdateMissionTypeEnum.UpdateMissionAugmentByVision
      ) as MissionAugmentByVisionState[];
    },
    cleanupExpiredPayloads: (state) => {
      state.missionSchedule = filterExpiredPayloads(state.missionSchedule);
      state.missionCuttingHeight = filterExpiredPayloads(state.missionCuttingHeight);
      state.mowerCuttingHeight = filterExpiredPayloads(state.mowerCuttingHeight);
      state.mowerSchedule = filterExpiredPayloads(state.mowerSchedule);
      state.mowingBehaviourSettings = filterExpiredPayloads(state.mowingBehaviourSettings);
      state.missionEnabled = filterExpiredPayloads(state.missionEnabled);
      state.missionAugmentByVision = filterExpiredPayloads(state.missionAugmentByVision);
    },
  },
});

export const {
  assignMissionOperationId,
  assignOperationId,
  outdateOperations,
  updateMissionOperation,
  updateOperation,
  clearOperation,
  registerOperation,
  clearMissionOperation,
  clearOperations,
  clearOperationsByStatuses,
  updateOperationStatusById,
  handleOperationExecuteResponse,
  registerMowingBehaviourSettings,
  cleanupForMowingBehaviourSettingsMission,
  cleanupEffectiveOperations,
  registerMissionEnabled,
  cleanupForMissionEnabled,
  registerMissionAugmentByVision,
  cleanupForMissionAugmentByVision,
  registerMissionCuttingHeight,
  cleanupForMissionCuttingHeight,
  reflectOperationStatusChange,
  registerMissionSchedule,
  cleanupForMissionSchedule,
  reflectAssetHfsActionsUpdate,
  cleanupExpiredPayloads,
  cleanupForMowerCuttingHeight,
  registerMowerCuttingHeight,
  cleanupForMowerSchedule,
  registerMowerSchedule,
  clearOperationById,
  setResultNotificationSent,
} = reducer.actions;

export default reducer.reducer;
